import { Component, OnInit, inject } from '@angular/core';
import { FormBuilder, FormArray, FormControl, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { AbstractFormDialog, AbstractFormDialogData, AbstractDialogResultData } from '@next/core-lib/dialog';

import { startWith, map } from 'rxjs/operators';

import { DigitalProductSubscription, DigitalProductsDialogResult, IndexedDigitalProductSubscription, BemProduct } from '../../models';

export interface DigitalProductsDialogData extends AbstractFormDialogData {
  digitalProductSubscriptions: DigitalProductSubscription[];
  bemProducts: BemProduct[];
  downyMildewActivated: boolean;
  virusMonitorActivated: boolean;
}

@Component({
  selector: 'app-digital-products-dialog',
  templateUrl: './digital-products-dialog.component.html',
  styleUrls: ['./digital-products-dialog.component.scss'],
  standalone: false,
})
export class DigitalProductsDialogComponent extends AbstractFormDialog<DigitalProductsDialogResult> implements OnInit {
  data = inject<DigitalProductsDialogData>(MAT_DIALOG_DATA);
  private readonly formBuilder = inject(FormBuilder);
  protected readonly mdDialogRef = inject<MatDialogRef<DigitalProductsDialogComponent, AbstractDialogResultData<DigitalProductsDialogResult>>>(MatDialogRef);

  formGroup = this.formBuilder.group({
    includeInOrder: this.formBuilder.array(this.data.digitalProductSubscriptions.map((requisite) => [requisite.includeInOrder, [Validators.required]])),
    correction: this.formBuilder.array(this.data.digitalProductSubscriptions.map((requisite) => [requisite.correction, [Validators.required]])),
  });

  digitalProductSubscriptionFilter = new FormControl();
  hasFilter = false;
  filteredDigitalProductSubscriptions$ = this.digitalProductSubscriptionFilter.valueChanges.pipe(
    startWith(''),
    map(
      (search: string) =>
        [search, this.data.digitalProductSubscriptions.map((requisite, index) => ({ ...requisite, index }))] as [string, IndexedDigitalProductSubscription[]],
    ),
    map(([search, requisites]) =>
      search ? requisites.filter((requisite) => requisite.skuName.toUpperCase().indexOf(search.toUpperCase()) !== -1) : requisites,
    ),
  );
  displayedColumns = ['name', 'includeInOrder', 'totalRequisite', 'correction', 'alreadyOrdered', 'toOrder'];
  ngOnInit(): void {
    this.digitalProductSubscriptionFilter.valueChanges.subscribe((value) => {
      this.hasFilter = value !== undefined && value !== null && value !== '';
    });
  }

  getIncludeInOrderFormArray(): FormArray {
    return this.formGroup.controls.includeInOrder as FormArray;
  }

  getCorrectionFormArray(): FormArray {
    return this.formGroup.controls.correction as FormArray;
  }

  getIncludeInOrderControl(i: number): FormControl {
    return this.getIncludeInOrderFormArray().controls[i] as FormControl;
  }

  getCorrectionControl(i: number): FormControl {
    return this.getCorrectionFormArray().controls[i] as FormControl;
  }

  getIncludeInOrder(i: number): number {
    return this.getIncludeInOrderControl(i).value as number;
  }

  getCorrection(i: number): number {
    return this.getCorrectionControl(i).value as number;
  }

  getToOrderAmount(i: number, totalRequisite, alreadyOrdered): number {
    const correction = this.getCorrection(i);

    return totalRequisite - alreadyOrdered - correction;
  }

  process(data) {
    const productSubscriptions = this.data.digitalProductSubscriptions.map(
      (productSubscription, i): DigitalProductSubscription => ({
        ...productSubscription,
        correction: data.correction[i],
        includeInOrder: data.includeInOrder[i],
      }),
    );
    super.confirm({ digitalProductSubscriptions: productSubscriptions });
  }
}
